import {
	EquirectangularReflectionMapping,
	LinearFilter,
	LinearMipmapLinearFilter,
	Texture,
	UVMapping,
	WebGLRenderer
} from 'three';

import { TextureType } from '../../../../model/enums';
import type { TextureInfo } from '../../../../model/texture-info';
import { getAnisotropyForQuality } from '../../../../hooks/use-three-kitchen/quality';

/**
 * Compressed GPU texture formats (BC7/ASTC/ETC2 — what KTX2 transcodes to)
 * don't support runtime mipmap generation via glGenerateMipmap; calling it
 * produces GL_INVALID_OPERATION and the upload fails, leaving the material
 * untextured. If the KTX2 file ships a baked mip chain (POT dims), Three.js
 * uses those; otherwise we drop to LinearFilter so minification still looks
 * reasonable without triggering the invalid op.
 *
 * Three.js KTX2Loader produces CompressedTexture instances, identified via
 * `isCompressedTexture`. Apply after any other filter/mipmap configuration.
 */
const applyCompressedTextureSafety = (texture: Texture): void => {
	if (!(texture as unknown as { isCompressedTexture?: boolean }).isCompressedTexture) return;
	texture.generateMipmaps = false;
	if (!texture.mipmaps || texture.mipmaps.length <= 1) {
		texture.minFilter = LinearFilter;
	}
};

/**
 * Configure sky texture
 */
export const configureSkyTexture = (texture: Texture): void => {
	texture.mapping = 300; // UVMapping
	texture.repeat.set(1, 1);
	texture.wrapS = 1001; // ClampToEdgeWrapping
	texture.wrapT = 1001;
	texture.offset.set(0, 0);
	applyCompressedTextureSafety(texture);
	texture.needsUpdate = true;
};

/**
 * Configure standard texture
 */
export const configureStandardTexture = (
	texture: Texture,
	textureInfo: TextureInfo,
	renderQuality: number,
	renderer: WebGLRenderer | null
): void => {
	const repeatBase = 0.045;
	const repeatScale = textureInfo.scale != null ? repeatBase * textureInfo.scale : 1;

	if (textureInfo.textureType === TextureType.Counter) {
		texture.wrapS = 1002; // MirroredRepeatWrapping
		texture.wrapT = 1002;
	} else {
		texture.wrapS = 1000; // RepeatWrapping
		texture.wrapT = 1000;
	}

	if (textureInfo.scale != null) {
		texture.repeat.set(repeatScale, repeatScale);
		texture.offset.set(0.5, 0.5);
	} else {
		texture.repeat.set(1, 1);
		texture.offset.set(0, 0);
	}

	// Use UVMapping for all surface textures (Floor, Surround, Counter, Trim)
	// EquirectangularReflectionMapping is only for environment/sky maps
	if (textureInfo.textureType === TextureType.Floor) {
		texture.mapping = UVMapping;
		texture.minFilter = LinearMipmapLinearFilter;
		texture.magFilter = LinearFilter;
		texture.generateMipmaps = true;
	} else {
		// Surround, Counter, Trim all use UVMapping
		texture.mapping = UVMapping;
	}

	texture.anisotropy = getAnisotropyForQuality(renderQuality, renderer);
	applyCompressedTextureSafety(texture);
	texture.needsUpdate = true;
};

/**
 * Configure map texture (alpha, normal, displacement)
 */
export const configureMapTexture = (
	texture: Texture,
	textureInfo: TextureInfo,
	renderQuality: number,
	renderer: WebGLRenderer | null
): void => {
	if (textureInfo.textureType === TextureType.Counter) {
		texture.wrapS = 1002;
		texture.wrapT = 1002;
	} else {
		texture.wrapS = 1000;
		texture.wrapT = 1000;
	}

	// Match Haxe behavior: only apply repeat/offset when scale is specified
	// When scale is null, Haxe computes repeatBase * null = 0, effectively
	// disabling the normal map by sampling a single texel
	if (textureInfo.scale != null) {
		const repeatBase = 0.045;
		const repeatScale = repeatBase * textureInfo.scale;
		texture.repeat.set(repeatScale, repeatScale);
		texture.offset.set(0.5, 0.5);
	}

	texture.anisotropy = getAnisotropyForQuality(renderQuality, renderer);
	applyCompressedTextureSafety(texture);
};

/**
 * Count textures to load
 */
export const countTexturesToLoad = (
	textureInfos: TextureInfo[],
	textures: Map<string, Texture>,
	alphaMaps: Map<string, Texture>,
	normalMaps: Map<string, Texture>,
	displacementMaps: Map<string, Texture>
): number => {
	let count = 0;
	for (const tx of textureInfos) {
		if (!textures.has(tx.key) && tx.texture) count++;
		if (!alphaMaps.has(tx.key) && tx.alphaMapPath) count++;
		if (!normalMaps.has(tx.key) && tx.normalMapPath) count++;
		if (!displacementMaps.has(tx.key) && tx.displacementMapPath) count++;
	}
	return count;
};
